<template>
  <div class="bills-overview">
    <div class="bills-overview-top">
      <h3 class="bills-overview-title ">{{ $t('billsOverview.billsOverviewTitle') }}</h3>
      <div v-if="!prepaidType" class="bills-total-payment">
        <h3>{{ $t('billsOverview.totalContractDebt') }} <span>{{ totalBillDebt | formatAmount }}€</span></h3>
        <base-button variant="primary" class="wide-bills-button" :disabled="!totalBillDebt" @click="payTotalDebt">
          {{ $t('billsOverview.payTotalDebtButton') }}
        </base-button>
      </div>
    </div>
    <div v-if="!prepaidType" class="bills-wrapper">
      <div class="bills-container">
        <p class="bills-overview-description">{{ $t('billsOverview.billsOverviewDescription') }}</p>
        <transition name="fade" mode="out-in">
          <div v-if="!dataLoading">
            <div v-for="(customer, index) in customerData" class="bill-service-overview" :key="'customer' + index">
              <div v-if="Object.keys(customer.service).length && customer.service.type !== 'MOBILE_PREPAID'"
                   class="customer-overview">
                <div class="bill-service-header">
                  <div class="bill-service-name">
                    <h3 class="header-title">{{ customer.service.firstName }}&nbsp;{{
                        customer.service.lastName || ''
                      }}</h3>
                    <p class="header-subtitle">{{ customer.service.title }}</p>
                  </div>
                  <div class="bill-service-actions">
                    <p>{{ $t('billsOverview.debt') }} : {{ customer.totalDebt.value | formatAmount }}€</p>
                    <div class="actions-wrapper">
                      <button @click="getSettledBills(customer.service.customerId)">
                        {{ $t('billsOverview.settledBillsLabel') }}
                      </button>
                      <button @click.prevent="handlePayCustomAmount(customer)">{{
                          $t('billsOverview.payAmountLabel')
                        }}
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="customer.bills.length || customer.totalDebt.value > 0" class="unsettled-bills">
                  <div v-for="(card, idx) in customer.bills"
                       class="bill-card"
                       :key="'unsBill' + index + idx">
                    <div class="bill-card-header">
                      <h2 class="bill-card-title">{{ $t('months.' + card.month) }}&nbsp;{{ card.year }}</h2>
                      <base-checkbox v-model="card.selected" variant="red" v-if="card.status !== 'PROCESSING'"
                                     class="bills-checkbox"/>
                    </div>
                    <div class="bill-card-body">
                      <h4 class="debt-amount">{{ card.debt.value | formatAmount }}<span class="currency"> €</span></h4>
                      <span v-if="card.status === 'NOT_PAID'">
                      {{ $t('billsOverview.notPaidLabel') }}
                    </span>
                      <span v-else-if="card.status === 'PARTIALLY_PAID'">
                      {{ $t('billsOverview.partiallyPaidLabel') }}
                    </span>
                      <span v-else>
                      {{ $t('billsOverview.paymentProcessLabel') }}
                    </span>
                    </div>
                    <div class="bill-card-footer">
                      <base-button variant="primary" class="wide-button" size="small"
                                   @click="paySingleBill(card, customer)" :disabled="card.status === 'PROCESSING'">
                        {{ $t('billsOverview.payBillBtn') }}
                      </base-button>
                      <base-button variant="text" class="download-button" @click="downloadBill(card.invoiceNumber)"
                                   aria-label="Download bill">
                        <i class="icon-sc-download-hover"></i>
                      </base-button>
                    </div>
                  </div>
                </div>
                <div v-else class="no-debt-text">{{ $t('billsOverview.noUnsettledBillsLabel') }}</div>
              </div>
            </div>
          </div>
          <loader-with-message v-else
                               :key="'loader'"
                               :has-error="dataFetchError"
                               @retry="getUnsettledBills"/>
        </transition>
      </div>
      <transition name="fade" mode="out-in">
        <div v-if="selectedBillsTotalCount" class="selected-bills-box">
          <div class="selected-bills-wrapper">
            <h3 class="title">{{ $t('billsOverview.totalAmountLabel') }}&nbsp;<span
              class="total-amount">{{ selectedBillsTotalAmount | formatAmount }}&nbsp;{{ $t('billsOverview.currency') }}</span></h3>
            <p class="subtitle"
               v-html="$tc('billsOverview.pickedContractsLabel', selectedBillsTotalCount,  {bills: selectedBillsTotalCount})"></p>
          </div>
          <div class="box-btn-wrapper">
            <base-button v-if="selectedBillsTotalCount" class="deselect-btn" @click="deselectBills">
              <img src="/assets/images/red-close.svg" alt=""/>{{ $t('billsOverview.deselectBillsBtn') }}
            </base-button>
            <base-button variant="primary"
                         class="box-btn"
                         @click="payMultipleBills">
              {{ $t('billsOverview.payBillsBoxBtn') }}
            </base-button>
          </div>
        </div>
      </transition>
    </div>
    <div v-else class="prepaid-users-message">
      <img src="/assets/images/no-results.svg" width="112" height="112" alt=""/>
      <h3 class="title">{{ $t('billsOverview.noResultsTitle') }}</h3>
      <p class="description">{{ $t('billsOverview.noResultsSubtitle') }}</p>
    </div>
    <modal
      class="settled-bills-modal"
      size="lg"
      :visible="settledBillsModal"
      @modal-close="settledBillsModal = false">
      <div class="settled-table-title">
        <h2>{{ $t('billsOverview.settledBillsTitle') }}</h2>
      </div>
      <div class="settled-bills-container">
        <table class="table settled-bills-table">
          <thead>
          <tr>
            <th>{{ $t('billsOverview.settledBillsMonth') }}</th>
            <th>{{ $t('billsOverview.settledBillsAmount') }}</th>
            <th>{{ $t('billsOverview.downloadSettledBill') }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(settledBill, index) in settledBillsList.bills" :key="'settledBill' + index">
            <td>{{ $t('months.' + settledBill.month) }}&nbsp;{{ settledBill.year }}</td>
            <td>{{ settledBill.billingAmount.value }}€</td>
            <td>
              <base-button variant="text" slot="action" class="download-button"
                           @click="downloadBill(settledBill.invoiceNumber)" aria-label="Download bill">
                <i class="icon-sc-download-hover"></i>
              </base-button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </modal>

    <payment-modal ref="paymentModal"
                   :visible="paymentModalVisible"
                   :payment-amount="payAmount"
                   :loading="paymentLoading"
                   size="md"
                   reverse-actions
                   has-cancel-button
                   @close="handleClosePaymentModal"
                   @payment-with-saved-card="handlePayWithCard"
                   @payment-with-new-card="handlePayWithNewCard"
                   :amount-slot-visible="customAmountSlotVisible">
      <div slot="amount" class="custom-amount">
        <p v-html="$t('billsOverview.paymentModalCustomAmountDescription', { name: payingCustomer })"
           class="custom-description"></p>
        <validation-observer ref="customAmountObserver" tag="form" class="custom-amount-form"
                             @submit.prevent="handleCustomAmountSubmit">
          <base-v-input v-model.trim.number="customAmount"
                        :label="$t('billsOverview.customAmountLabel')"
                        class="custom-amount-field"
                        id="amount"
                        name="amount"
                        :submitted="customAmountFormSubmitted"
                        rules="required|numeric|max:15|min_value:1"
                        maxlength="15"
                        v-autofocus/>
          <base-button variant="primary" type="submit" class="custom-amount-button">{{
              $t('common.continue')
            }}
          </base-button>
        </validation-observer>
      </div>
      <template v-if="paymentType === paymentTypes.standalone">
        <template slot="title">{{ $t('billsOverview.paymentModalSingleBillTitle', { month: payMonth }) }}</template>
        <span slot="description"
              v-html="$t('billsOverview.paymentModalSingleBillDescription', { amount: payAmount })"></span>
        <span slot="noCardDescription"
              v-html="$t('billsOverview.paymentModalSingleBillDescription', { amount: payAmount })"></span>
      </template>
      <template v-else-if="paymentType === paymentTypes.multiple">
        <template slot="title">{{ $t('billsOverview.paymentModalMultipleBillTitle') }}</template>
        <span slot="description"
              v-html="$t('billsOverview.paymentModalMultipleBillDescription', { amount: payAmount })"></span>
        <span slot="noCardDescription"
              v-html="$t('billsOverview.paymentModalMultipleBillDescription', { amount: payAmount })"></span>
      </template>
      <template v-else-if="paymentType === paymentTypes.total">
        <template slot="title">{{ $t('billsOverview.paymentModalTotalBillTitle') }}</template>
        <span slot="description"
              v-html="$t('billsOverview.paymentModalTotalBillDescription', { amount: payAmount })"></span>
        <span slot="noCardDescription"
              v-html="$t('billsOverview.paymentModalTotalBillDescription', { amount: payAmount })"></span>
      </template>
      <template v-else-if="paymentType === paymentTypes.custom">
        <template slot="title">{{ $t('billsOverview.paymentModalCustomBillTitle') }}</template>
        <span slot="description"
              v-html="$t('billsOverview.paymentModalCustomBillDescription', { amount: payAmount })"></span>
        <span slot="noCardDescription"
              v-html="$t('billsOverview.paymentModalCustomBillDescription', { amount: payAmount })"></span>
      </template>
      <div slot="special" class="modal-pages" v-show="!paymentLoading">
        <span :class="{'active': customAmountSlotVisible}" @click="backToCustomSlot"></span>
        <span :class="{'active': !customAmountSlotVisible}"></span>
      </div>
    </payment-modal>

    <bank-form :form-data="bankAuthForm" ref="bankForm"/>

    <success-modal :visible="paymentSuccessModalVisible"
                   @close="handleSuccessModalClose"
                   :success-title="paymentSuccessTitle"
                   :success-description="paymentSuccessDescription"/>
  </div>
</template>

<script>
import errorMessageHandler from '@/mixins/errorMessageHandler'
import fileDownloader from '@/mixins/fileDownloader'
import { addDataLayerEvent } from '@/services/dataLayer'
import Modal from '@/components/common/modal/modal'
import PaymentModal from '@/components/common/payment-cards/payment-modal'
import BankForm from '@/components/common/payment-cards/bank-form'
import SuccessModal from '@/components/common/modal/success-modal'
import billPaymentHandler from '@/components/bills/billPaymentHandler'
import formatCurrency from '@/filters/formatCurrency'
import LoaderWithMessage from '@/components/common/loader-with-message'

export default {
  name: 'bills-overview',
  components: { LoaderWithMessage, SuccessModal, BankForm, PaymentModal, Modal },
  data () {
    return {
      selectedBill: [],
      customerData: [],
      settledBillsList: [],
      totalBillDebt: null,
      settledBillsModal: false,
      prepaidType: false,
      processingStatus: false,
      paymentModalVisible: false,
      payingCustomer: '',
      payAmount: '',
      payMonth: '',
      paymentInvoiceNumber: '',
      paymentLoading: false,
      bankAuthForm: {},
      paymentSuccessModalVisible: false,
      paymentSuccessTitle: '',
      paymentSuccessDescription: '',
      paymentType: '',
      customAmountSlotVisible: undefined,
      customerId: '',
      customAmount: '',
      customAmountFormSubmitted: false,
      dataLoading: false,
      dataFetchError: false
    }
  },
  mixins: [errorMessageHandler, fileDownloader, billPaymentHandler],
  filters: { ...formatCurrency },
  computed: {
    selectedBillsTotalAmount () {
      let totalSelectedBills = 0
      this.customerData.forEach(customer => {
        customer.bills.forEach(bill => {
          if (bill.selected && bill.debt && bill.debt.value && bill.status !== 'PROCESSING') totalSelectedBills += bill.debt.value
        }, 0)
      })
      return totalSelectedBills.toFixed(2).replace('.', ',')
    },
    selectedBillsTotalCount () {
      let totalSelectedBills = 0
      this.customerData.forEach(customer => {
        customer.bills.forEach(bill => {
          if (bill.selected && bill.status !== 'PROCESSING') totalSelectedBills++
        }, 0)
      })
      return totalSelectedBills
    },
    paymentTypes () {
      return {
        standalone: 'standalone',
        multiple: 'multiple',
        total: 'total',
        custom: 'custom'
      }
    }
  },
  methods: {
    deselectBills () {
      this.customerData.forEach(customer => {
        customer.bills.forEach(bill => {
          if (bill.selected) {
            this.$set(bill, 'selected', false)
          }
        })
      })
    },
    getUnsettledBills () {
      this.dataLoading = true
      this.dataFetchError = false
      this.$apiRequest.get('user/bills/unsettled')
        .then(response => {
          this.customerData = response.data.customerBills
          this.totalBillDebt = response.data.accountDebt.value
          this.getServiceType()
          for (const el of this.customerData) {
            for (const bill of el.bills || []) {
              if (bill.status === 'PROCESSING') {
                this.processingStatus = true
                break
              }
            }
          }

          this.dataLoading = false
        })
        .catch(() => {
          this.dataFetchError = true
          // this.handleErrorMessage(e.response.data)
        })
    },
    getServiceType () {
      const prepaidType = this.customerData.every(service => service.service.type === 'MOBILE_PREPAID')
      if (prepaidType) {
        this.prepaidType = true
      }
    },
    getSettledBills (customerId) {
      this.$apiRequest.get(`user/bills/settled?customerId=${customerId}`)
        .then(response => {
          this.settledBillsList = response.data
          this.settledBillsModal = true
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    downloadBill (invoiceNumber) {
      this.$apiRequest.get(`user/bills/pdf?invoiceNumber=${invoiceNumber}`, { responseType: 'arraybuffer' })
        .then(response => {
          const disposition = response.headers['content-disposition']
          const fileName = disposition.split('filename=')[1].split(';')[0]
          if (response.status === 200) {
            addDataLayerEvent('preuzmi_racun')
            this.downloadFile(response, fileName) // fileDownloader mixin
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    paySingleBill (card, customer) {
      this.paymentType = this.paymentTypes.standalone
      this.payingCustomer = customer.service.firstName ? `${customer.service.firstName} ${customer.service.lastName}` : customer.service.description
      this.payAmount = card.debt.formattedValue
      this.payMonth = (`${this.$t('months.' + card.month)} ${card.year}`).toLowerCase()
      this.paymentInvoiceNumber = card.invoiceNumber
      this.paymentModalVisible = true
    },
    payMultipleBills () {
      this.paymentType = this.paymentTypes.multiple
      this.payAmount = this.$options.filters.formatAmount(this.selectedBillsTotalAmount)
      this.paymentModalVisible = true
    },
    payTotalDebt () {
      this.paymentType = this.paymentTypes.total
      this.payAmount = this.$options.filters.formatAmount(this.totalBillDebt)
      this.paymentModalVisible = true
    },
    getSelectedUnpaidBills () {
      const selectedBills = []
      this.customerData.forEach(customer => {
        customer.bills.forEach(bill => {
          if (bill.selected && bill.status === 'NOT_PAID') {
            selectedBills.push(bill.invoiceNumber)
          }
        })
      })
      return selectedBills
    },
    handlePayWithCard (cardCode) {
      if (this.paymentType === this.paymentTypes.standalone) {
        this.payStandaloneBillWithSavedCard(cardCode)
        addDataLayerEvent({
          type: 'single',
          event: 'bills_payment_click'
        })
      } else if (this.paymentType === this.paymentTypes.multiple) {
        const selectedBills = this.getSelectedUnpaidBills() || []
        this.payMultipleBillsWithSavedCard(cardCode, selectedBills)
        addDataLayerEvent({
          type: 'several',
          event: 'bills_payment_click'
        })
      } else if (this.paymentType === this.paymentTypes.total) {
        this.payTotalBillWithSavedCard(cardCode)
        addDataLayerEvent({
          type: 'all',
          event: 'bills_payment_click'
        })
      } else if (this.paymentType === this.paymentTypes.custom) {
        this.payCustomBillWithSavedCard(cardCode, this.customAmount, this.customerId)
        addDataLayerEvent({
          type: 'custom',
          event: 'bills_payment_click'
        })
      }
    },
    handlePayWithNewCard (options) {
      if (this.paymentType === this.paymentTypes.standalone) {
        this.payStandaloneBillWithNewCard(options)
        addDataLayerEvent({
          type: 'single',
          event: 'bills_payment_click'
        })
      } else if (this.paymentType === this.paymentTypes.multiple) {
        const selectedBills = this.getSelectedUnpaidBills() || []
        this.payMultipleBillsWithNewCard(options, selectedBills)
        addDataLayerEvent({
          type: 'several',
          event: 'bills_payment_click'
        })
      } else if (this.paymentType === this.paymentTypes.total) {
        this.payTotalBillWithNewCard(options)
        addDataLayerEvent({
          type: 'all',
          event: 'bills_payment_click'
        })
      } else if (this.paymentType === this.paymentTypes.custom) {
        this.payCustomBillWithNewCard(options, this.customAmount, this.customerId)
        addDataLayerEvent({
          type: 'custom',
          event: 'bills_payment_click'
        })
      }
    },
    handleSuccessModalClose () {
      this.paymentSuccessModalVisible = false
      this.payAmount = ''
    },
    handleClosePaymentModal () {
      this.paymentModalVisible = false
      this.customAmountSlotVisible = undefined
      this.customAmount = ''
      this.customerId = ''
      this.customAmountFormSubmitted = false
    },
    async handleCustomAmountSubmit () {
      const formValid = await this.$refs.customAmountObserver.validate()
      this.customAmountFormSubmitted = true
      if (formValid) {
        if (this.$refs.paymentModal.paymentCards.length) {
          this.customAmountSlotVisible = false
        } else {
          // to simplify things, emit event from payment modal
          this.$refs.paymentModal.emitPayWithNewCard()
        }
      }
    },
    handlePayCustomAmount (customer) {
      this.customerId = customer.service.customerId
      this.customAmountSlotVisible = true
      this.paymentType = this.paymentTypes.custom
      this.payingCustomer = customer.service.firstName ? `${customer.service.firstName} ${customer.service.lastName || ''}` : customer.service.description
      this.paymentModalVisible = true
    },
    backToCustomSlot () {
      if (!this.customAmountSlotVisible) this.customAmountSlotVisible = true
    }
  },
  mounted () {
    this.getUnsettledBills()
  }
}
</script>

<style scoped lang="scss" src="./bills-comp-styles/bills-overview.scss"></style>
